import { userInfoStore } from '../../../store/userInfoStore';
import { crashGameStore } from '../crashStore/gameStore';
import { RoundStatus } from '../../../core/constants/roundStatus';
import { chartStore } from '../crashStore/chartStore';
import { crashBetsStore } from '../crashStore/betStore';
import { BetStatus } from '../../../core/constants/betStatus';
import { crashGameConnection } from '../crashConnection';
import { autoBetStore } from '../../../store/autoBetStore';
import { worker } from '../../../index';
import { notificationStore } from '../../../store/notificationStore';
import { maskName } from '../../../core/helpers/maskName';
import { calculateWonUserCoordinatesOnParabola } from '../crashCore/helpers/supporters';
import { checkHashStore } from '../../../store/checkHashStore';
import {
  CRASH_DEFAULT_WIN_CHANCE_VALUE,
  crashCountdownDeductionValues,
  WIN_CHANCE_LIMIT,
} from '../crashCore/constants';
import { THIS_GAMES_COLUMN_ID } from '../../../core/constants/liveBetsContants';
import { soundService } from '../../../services/baseSoundService/soundService';
import { CrashSoundsName } from '../crashCore/constants/sounds';
import { betHistoryStore } from '../../../store/betHistoryStore';
import { liveUpdateStore } from '../../../store/liveUpdateStore';
import {
  ResponseTypeForGamesByRound,
  ValidationResponseCodeForGameByRound,
} from '../../../core/constants/validationType';
import { parseNumberByPrecision } from '../../../core/helpers/parseNumberByPrecision';
import { chartAnimate } from '../crashCore/constants/chartAnimate';
import { mainGameConnection } from '../../../core/mainGameConnection/mainGameConnection';
import { freeBetStore } from '../../../store/freeBetStore';

class CrashGameService {
  onGetInitialStateResult({ results, roundInfo, currentBets }) {
    crashBetsStore.resetStore();
    if (!roundInfo) {
      setTimeout(() => {
        mainGameConnection.sendGameGetInitialState(false).then(() => {});
      }, 500);
    } else {
      const { status, startedDate, second, odd } = roundInfo;
      const { onHold, onFinish, finished, started } = RoundStatus;
      chartStore.resetChartData();
      crashGameStore.setGameLatestResults(results);
      const {
        resultHash,
        resultKey,
        resultSalt,
        roundId,
        roundData,
        startedDate: lastStartedDate,
      } = results.filter((item) => item.status === RoundStatus.finished)?.[0] || '';
      checkHashStore.setNextRoundHash(
        results.filter((item) => item.status === RoundStatus.started)?.[0]?.resultHash || ''
      );
      checkHashStore.setLastRoundInfo({
        salt: resultSalt,
        hash: resultHash,
        key: resultKey,
        roundId,
        roundData,
        startedDate: lastStartedDate,
      });
      crashGameStore.updateCurrentRoundStatus(status);
      crashGameStore.setRoundId(roundInfo?.roundId);
      if (currentBets?.length) {
        const userCurrentBet = currentBets.find(
          (item) =>
            item?.playerExternalId === userInfoStore.userId &&
            !item.winAmount &&
            item.status === BetStatus.Pending
        );
        if (userCurrentBet) {
          crashBetsStore.updateBetAccepted(true, userCurrentBet?.id);
          crashBetsStore.setMultiplier(userCurrentBet.betData.autoCashoutOdd);
          userInfoStore.setBetAmount(userCurrentBet.amount);
        }
      }
      if (status === onHold) {
        crashBetsStore.setIsBetsClosed(true);
        chartStore.setLaunchTime(+new Date(startedDate));
        chartStore.setIsStarted(true);
        chartStore.updateAnimationStartedAt((chartStore.launchTime - Date.now()) / 1000);
      } else if ([onFinish, finished].includes(status)) {
        crashBetsStore.updateIsDisabledBet(true);
        chartStore.setIsRoundFinish(true);
        crashGameStore.setRoundFinishedData({
          finishedOdd: odd,
          finishedOddStatus: 'lose',
          currentUserData: null,
        });
      } else if (status === started) {
        if (second > 1) {
          // soundService.play(CrashSoundsName.timer);
        }
        chartAnimate.handleAxisAnimations();
        const isBetClosed = second < 0;
        if (isBetClosed) {
          crashBetsStore.setIsBetsClosed(true);
          crashBetsStore.updateIsDisabledBet(true);
        } else {
          crashBetsStore.setIsBetsClosed(false);
          crashBetsStore.updateIsDisabledBet(crashBetsStore.betData.isBetAccepted);
        }
      }

      if (started === status && second > 0) {
        crashGameStore.setTimerDuration(roundInfo.duration);
        const time = Math.floor(Math.abs(roundInfo.second));
        crashGameStore.setTimeData(time);
        this.sendWorker({ timerValue: time, ...crashCountdownDeductionValues });
      } else {
        this.sendWorker();
        crashGameStore.setTimerDuration(roundInfo.duration);
        crashGameStore.setTimeData(null);
      }
      liveUpdateStore.setSelectedColumn(THIS_GAMES_COLUMN_ID);
    }
  }

  handleDoBetResult(data) {
    const { id, playerExternalId } = data;
    const { isAutoBetOn, autoBetCount, setAutoBetCount, isAutoBetStart, resetAutoBetState } =
      autoBetStore;
    const { userId } = userInfoStore;
    if (Number(playerExternalId) === Number(userId)) {
      if (data.cryptoFreeBetInfo !== null) {
        freeBetStore.updateCurrentFreeBet(data.cryptoFreeBetInfo);
      }
      crashBetsStore.setIsAllowDoBet(true);
      crashBetsStore.handleBetForNextRound(false);
      crashBetsStore.updateBetAccepted(true, id);
      crashBetsStore.updateIsDisabledBet(true);
      // liveUpdateStore.updateLiveSpinsByGame(data);
      if (isAutoBetOn && autoBetCount && isAutoBetStart) {
        setAutoBetCount(autoBetCount - 1);
        if (!(autoBetCount - 1)) {
          resetAutoBetState();
        }
      }
    }
  }

  handleDoBetResultValidation({ responseCode }) {
    if (
      responseCode !== ValidationResponseCodeForGameByRound[[ResponseTypeForGamesByRound.Success]]
    ) {
      autoBetStore.resetAutoBetState();
    }
    crashBetsStore.resetDoBetData();
    crashBetsStore.setIsAllowDoBet(true);
    notificationStore.setNotification(responseCode);
  }

  doBetSender() {
    if (crashBetsStore.isAllowDoBet) {
      soundService.play(CrashSoundsName.bet);
      crashBetsStore.setIsAllowDoBet(false);
      mainGameConnection.doBet(crashBetsStore.buildDoBetModel());
    }
  }

  cashOutSender() {
    const { betId } = crashBetsStore.betData;
    const { roundId } = crashGameStore;
    crashGameConnection.sendCashOut({ betId, roundId });
  }

  handleRoundStart({
    data: {
      results,
      startedRound: { roundId },
    },
  }) {
    const { isAutoBetOn, autoBetCount, isAutoBetStart, resetAutoBetState } = autoBetStore;
    const {
      betData: { isNextRoundBet },
      handleBetForNextRound,
    } = crashBetsStore;

    // soundService.play(CrashSoundsName.timer);
    // liveUpdateStore.setLiveSpinsByGame([]);
    chartStore.updateAnimationStartedAt(0);
    crashGameStore.resetRoundFinishedData();
    this.sendWorker({ timerValue: crashGameStore.timerDuration, ...crashCountdownDeductionValues });
    chartStore.resetChartData();
    chartAnimate.handleAxisAnimations();
    crashBetsStore.updateIsDisabledBet(false);
    crashBetsStore.updateBetAccepted(false);
    crashGameStore.setRoundId(roundId);
    crashGameStore.updateLatestResults(
      results.filter((item) => item.status === RoundStatus.finished)[0]
    );
    const {
      resultHash,
      resultKey,
      resultSalt,
      roundData,
      startedDate: lastStartedDate,
      roundId: lastRoundId,
    } = results.filter((item) => item.status === RoundStatus.finished)?.[0] || '';
    checkHashStore.setNextRoundHash(
      results.filter((item) => item.status === RoundStatus.started)?.[0].resultHash
    );
    checkHashStore.setLastRoundInfo({
      salt: resultSalt,
      hash: resultHash,
      key: resultKey,
      roundData,
      roundId: lastRoundId,
      startedDate: lastStartedDate,
    });

    crashGameStore.resetRatingStarsCountWithText();
    if (isAutoBetOn && isAutoBetStart) {
      isNextRoundBet && handleBetForNextRound();
      if (autoBetCount) {
        this.doBetSender();
      } else {
        resetAutoBetState();
      }
    }
    if (crashBetsStore.betData.isNextRoundBet && !isAutoBetOn && !isAutoBetStart) {
      this.doBetSender();
    }
  }

  handleResultReport({
    data: {
      isCrash,
      resultReportData: { odd, startedDate, bets },
    },
  }) {
    const { lunchTime, isStarted } = chartStore;
    odd && crashGameStore.setOddData(odd);
    crashBetsStore.updateIsDisabledBet(false);
    if (!lunchTime || !isStarted) {
      chartStore.setLaunchTime(+new Date(startedDate));
      chartStore.setIsStarted(true);
    }
    chartAnimate.handleAxisAnimations();

    crashGameStore.decideRatingStarsCount(odd);

    if (crashBetsStore.betData.isBetAccepted) {
      crashBetsStore.setCurrentWinAmount(
        parseNumberByPrecision(odd, userInfoStore.betAmount, userInfoStore.precision)
      );
    }

    if (bets?.length) {
      const myBet = bets.find((item) => +userInfoStore.userId === +item.playerExternalId);
      if (myBet) {
        soundService.play(CrashSoundsName.win, true);
        crashGameStore.setWonBetOdd(myBet.odd);
        // liveUpdateStore.setLiveSpinsByGame(
        //   updateDataInArrayOfObjects(liveUpdateStore.liveSpinsByGame, myBet)
        // );
        setTimeout(() => {
          userInfoStore.getBalance();
        }, 2000);
        crashBetsStore.setCashedOutData({
          odd: myBet.odd,
          winAmount: parseNumberByPrecision(myBet.winAmount, 1, userInfoStore.precision),
        });
        crashBetsStore.updateBetAccepted(false, null);
        crashBetsStore.updateIsDisabledBet(false);
      }
      bets?.forEach((item) => {
        const coordinates = calculateWonUserCoordinatesOnParabola();
        if (
          !crashBetsStore.wonUsersList.find(
            (user) => user.userName === maskName(item?.playerExternalId)
          )
        ) {
          crashBetsStore.updateWonUsersList({
            userName: maskName(item?.userId),
            odd,
            ...coordinates,
          });
        }
      });
      // liveUpdateStore.setLiveSpinsByGame(bets);
    }
    if (isCrash) {
      chartStore.setIsCrashed(true, odd);
      // chartStore.updateAnimationStartedAt(0);
    }
  }

  handleRoundFin({
    data: {
      finishedRound: { roundData },
      bets,
    },
  }) {
    const currentUserData = bets.find(
      (userData) => Number(userData?.playerExternalId) === Number(userInfoStore.userId)
    );

    if (currentUserData) {
      setTimeout(() => {
        betHistoryStore.getLastBet();
      }, 5000);
    }
    crashGameStore.setRoundFinishedData({
      finishedOdd: roundData,
      currentUserData,
      finishedOddStatus: 'lose',
    });
    // if (bets?.length) {
    //   liveUpdateStore.setLiveSpinsByGame(
    //     bets?.map((item) => ({ ...item, finishedOdd: roundData }))
    //   );
    // }
    setTimeout(() => {
      crashBetsStore.resetWonUsersList();
    }, 1000);

    chartStore.setIsRoundFinish(true);
    crashBetsStore.setIsBetsClosed(false);
    crashBetsStore.updateIsDisabledBet(true);
    crashGameStore.setOddData(null);
    crashBetsStore.setCurrentWinAmount(null);
  }

  handleBetsClosed() {
    crashBetsStore.setIsBetsClosed(true);
    // soundService.stop(CrashSoundsName.timer);
  }

  // cashOutData
  handleCashOutResult(data) {
    const { odd, winAmount, playerExternalId, freeBetInfo } = data || {};
    const { userId } = userInfoStore;
    // const {
    //   chartDimensions: { width, height },
    // } = chartStore;
    crashGameStore.setWonBetOdd(odd);
    crashBetsStore.updateBetAccepted(false, null);
    crashBetsStore.updateIsDisabledBet(false);
    if (Number(userId) === Number(playerExternalId)) {
      if (freeBetInfo !== null) {
        freeBetStore.updateCurrentFreeBet(freeBetInfo);
      }
      soundService.play(CrashSoundsName.win);
      // liveUpdateStore.setLiveSpinsByGame(data);

      setTimeout(() => {
        userInfoStore.getBalance();
      }, 2000);
      crashBetsStore.setCashedOutData({ odd, winAmount });
      // const coordinates = calculateWonUserCoordinatesOnParabola(width, height, odd);
      // crashBetsStore.updateWonUsersList({
      //   userName: maskName(playerExternalId),
      //   odd,
      //   ...coordinates,
      // });
    }
  }

  handleCashOutResultQueue(doBetsResults) {
    doBetsResults?.forEach((item) => {
      const coordinates = calculateWonUserCoordinatesOnParabola();
      crashBetsStore.updateWonUsersList({
        userName: maskName(item?.userId),
        odd: item?.odd,
        ...coordinates,
      });
      // liveUpdateStore.setLiveSpinsByGame(
      //   updateDataInArrayOfObjects(liveUpdateStore.liveSpinsByGame, item)
      // );
    });
  }

  handleDoBetResultQueue() {
    // liveUpdateStore.setLiveSpinsByGame([...liveUpdateStore.liveSpinsByGame, ...doBetData]);
  }

  handleSpaceKeyDown() {
    if (
      !+crashBetsStore.winChance ||
      +crashBetsStore.winChance > WIN_CHANCE_LIMIT.maxBet ||
      +crashBetsStore.winChance < WIN_CHANCE_LIMIT.minBet
    ) {
      crashBetsStore.setWinChance(CRASH_DEFAULT_WIN_CHANCE_VALUE);
    }
    const { isAutoBetOn, autoBetCount, isAutoBetStart, setIsAutoBetStart, resetAutoBetState } =
      autoBetStore;
    const {
      betData: { isNextRoundBet, isBetAccepted },
      isBetsClosed,
    } = crashBetsStore;
    if (isAutoBetOn && autoBetCount) {
      if (isAutoBetOn && autoBetCount && !isAutoBetStart) {
        setIsAutoBetStart(true);
        return;
      }
      if (isAutoBetOn && autoBetCount && isAutoBetStart && !isBetAccepted) {
        resetAutoBetState();
        isNextRoundBet && crashBetsStore.handleBetForNextRound();
        return;
      }
    }
    if (!isBetAccepted && !isBetsClosed) {
      this.doBetSender();
    } else if (!isBetAccepted && isBetsClosed) {
      crashBetsStore.handleBetForNextRound();
    }
  }

  handleKeyQDown() {
    const {
      betData: { isBetAccepted },
    } = crashBetsStore;
    if (isBetAccepted) {
      this.cashOutSender();
    }
  }

  countDown({ data }) {
    crashGameStore.setTimeData(data?.timerValue);
  }

  sendWorker(countdownData) {
    if (countdownData) {
      worker.addEventListener('message', this.countDown);
      worker.postMessage(countdownData);
    } else {
      worker.removeEventListener('message', this.countDown);
    }
  }
}

export const crashGameService = new CrashGameService();
